/**
 * Span lookup — board/inspector entity id → authored declaration site.
 *
 * The wire model a board renders carries only `sourceFile` per node; the full
 * spans live on the AST decls the resolver keeps (`ResolvedModule.decl`). This
 * service resolves a board id back to that decl and its spans so mutation
 * synthesizers (and go-to-definition) are span-driven, never text-scanning.
 *
 * Board module ids are `moduleDiffId` values: the StableId when the module has
 * one, else the dotted qualified path (`Platform.Auth`). A leading `#` is
 * tolerated. Interface ids are dotted paths (`Orders.Api.create`); process ids
 * are `processIdentity` values (StableId / qualified name / anon signature).
 *
 * `materialization` classifies the §4a degenerate targets (WYSIWYG plan,
 * 2026-07-07): synthesizers refuse or compose a materialize precursor instead
 * of brace-scanning past a span-less or bodyless declaration.
 */

import type {
  InterfaceDecl,
  ModuleDecl,
  ProcessDecl,
  SourceSpan,
  StableId,
} from "./types.js";
import { processIdentity } from "./types.js";
import type { ViewDeclV2 } from "./v010-ast.js";
import type { ResolvedInterface, ResolvedModel, ResolvedModule } from "./resolver.js";
import { buildIndexes, type GraphIndexes } from "./graph.js";

/** How a value/insert op may address this declaration (§4a). */
export type Materialization =
  | "ready" // authored, braced body — every synthesizer may target it
  | "bodyless" // authored, no braces — insert ops must materialize a body
  | "stub"; // synthesized (zero span) — ops must promote a real decl first

export interface ModuleLocation {
  readonly kind: "module";
  readonly module: ResolvedModule;
  readonly decl: ModuleDecl;
  readonly file?: string;
  /** The decl's own span (prefix clauses excluded — see `extent`). */
  readonly span?: SourceSpan;
  /** The span delete/move must consume: `extentSource ?? source`. */
  readonly extent?: SourceSpan;
  readonly nameSpan?: SourceSpan;
  readonly materialization: Materialization;
}

export interface InterfaceLocation {
  readonly kind: "interface";
  readonly iface: ResolvedInterface;
  readonly owner: ResolvedModule;
  readonly decl: InterfaceDecl;
  readonly file?: string;
  readonly span?: SourceSpan;
  readonly extent?: SourceSpan;
  readonly nameSpan?: SourceSpan;
  readonly materialization: Materialization;
}

export interface ProcessLocation {
  readonly kind: "process";
  readonly decl: ProcessDecl;
  readonly file?: string;
  readonly span?: SourceSpan;
  readonly extent?: SourceSpan;
  readonly nameSpan?: SourceSpan;
  readonly materialization: Materialization;
}

export interface SpanLookup {
  moduleFor(boardId: string): ModuleLocation | undefined;
  interfaceFor(path: string): InterfaceLocation | undefined;
  processFor(id: string): ProcessLocation | undefined;
  /** View by `#id` or name (views are flat on the model). */
  viewFor(idOrName: string): ViewDeclV2 | undefined;
  /** v0.11: the space-qualified spellings (`Space::Path`) when a BARE dotted
   *  target names MORE THAN ONE landed module across spaces. Empty for a
   *  unique, spaced (`::`), or unknown target. Write paths refuse on >1 —
   *  never first-match-wins on a mutation; reads stay lenient. */
  moduleTwins(raw: string): readonly string[];
  /** Same, for a bare dotted interface path — ambiguity always stems from
   *  the OWNER module having cross-space twins that both carry the suffix. */
  interfaceTwins(raw: string): readonly string[];
}

/** A zero/absent span marks a synthesized decl (stubs stamp `{0,0}`). */
function isZeroSpan(span: SourceSpan | undefined): boolean {
  return !span || (span.start.offset === 0 && span.end.offset === 0);
}

function moduleMaterialization(m: ResolvedModule): Materialization {
  if (m.synthesized || isZeroSpan(m.decl.source)) return "stub";
  return m.decl.hasBody ? "ready" : "bodyless";
}

function interfaceMaterialization(i: ResolvedInterface): Materialization {
  if (i.synthesized || isZeroSpan(i.decl.source)) return "stub";
  return i.decl.hasBody ? "ready" : "bodyless";
}

export function createSpanLookup(model: ResolvedModel): SpanLookup {
  let indexes: GraphIndexes | undefined;
  const idx = (): GraphIndexes => (indexes ??= buildIndexes(model));

  // Bare dotted path → every landed module carrying it (across spaces). The
  // graph's modulesByPath keeps ONE winner per bare key, so twin detection
  // needs its own walk (lazy — only write paths ask).
  let bareModules: Map<string, { space?: readonly string[]; path: string }[]> | undefined;
  const bareIdx = (): Map<string, { space?: readonly string[]; path: string }[]> => {
    if (!bareModules) {
      bareModules = new Map();
      const visit = (m: ResolvedModule, parent: readonly string[]): void => {
        const path = [...parent, m.name];
        const key = path.join(".");
        const list = bareModules!.get(key) ?? [];
        list.push({ space: m.space, path: key });
        bareModules!.set(key, list);
        for (const child of m.modules) visit(child, path);
      };
      for (const m of model.modules) visit(m, []);
    }
    return bareModules;
  };
  const spacedSpelling = (c: { space?: readonly string[]; path: string }): string =>
    c.space && c.space.length > 0 ? `${c.space.join(".")}::${c.path}` : c.path;

  let processesById: Map<string, ProcessDecl> | undefined;
  const procIdx = (): Map<string, ProcessDecl> => {
    if (!processesById) {
      processesById = new Map();
      for (const p of model.processes) processesById.set(processIdentity(p), p);
    }
    return processesById;
  };

  function moduleLocation(m: ResolvedModule): ModuleLocation {
    return {
      kind: "module",
      module: m,
      decl: m.decl,
      file: m.decl.source?.file,
      span: m.decl.source,
      extent: m.decl.extentSource ?? m.decl.source,
      nameSpan: m.decl.nameSource,
      materialization: moduleMaterialization(m),
    };
  }

  return {
    moduleFor(boardId: string): ModuleLocation | undefined {
      const raw = boardId.startsWith("#") ? boardId.slice(1) : boardId;
      const byId = idx().modulesById.get(raw as StableId);
      if (byId) return moduleLocation(byId);
      const byPath = idx().modulesByPath.get(raw);
      return byPath ? moduleLocation(byPath) : undefined;
    },

    interfaceFor(path: string): InterfaceLocation | undefined {
      const iface = idx().interfacesByPath.get(path);
      if (!iface) return undefined;
      const owner = idx().interfaceOwner.get(iface);
      if (!owner) return undefined;
      return {
        kind: "interface",
        iface,
        owner,
        decl: iface.decl,
        file: iface.decl.source?.file ?? owner.decl.source?.file,
        span: iface.decl.source,
        extent: iface.decl.extentSource ?? iface.decl.source,
        nameSpan: iface.decl.nameSource,
        materialization: interfaceMaterialization(iface),
      };
    },

    viewFor(idOrName: string): ViewDeclV2 | undefined {
      const raw = idOrName.startsWith("#") ? idOrName.slice(1) : idOrName;
      // Match `#id`, then the space-qualified spelling (`Space::Owner.Name`,
      // v0.11 — the ONLY unambiguous address for a cross-space twin), then
      // the QUALIFIED name (owner path + name) so a module-scoped view
      // addressed as `M.V` resolves, then fall back to the bare name — the
      // same addressing order `computeViewSurfaceResult` uses.
      const spelled = (v: ViewDeclV2): string => {
        const qn = [...(v.ownerPath ?? []), v.name].join(".");
        return v.space && v.space.length > 0 ? `${v.space.join(".")}::${qn}` : qn;
      };
      return (
        model.views.find((v) => (v.id && (v.id as string) === raw) || spelled(v) === raw) ??
        model.views.find((v) => [...(v.ownerPath ?? []), v.name].join(".") === raw) ??
        model.views.find((v) => v.name === raw)
      );
    },

    moduleTwins(raw: string): readonly string[] {
      if (raw.includes("::") || raw.startsWith("#")) return [];
      // An exact `#id` hit is never ambiguous — ids address one entity.
      if (idx().modulesById.has(raw as StableId)) return [];
      const cands = bareIdx().get(raw) ?? [];
      return cands.length > 1 ? cands.map(spacedSpelling) : [];
    },

    interfaceTwins(raw: string): readonly string[] {
      if (raw.includes("::") || raw.startsWith("#")) return [];
      const segs = raw.split(".");
      // Longest owner prefix first — a nested module's own path wins over a
      // shorter twin prefix plus a longer interface chain.
      for (let i = segs.length - 1; i >= 1; i--) {
        const owners = bareIdx().get(segs.slice(0, i).join(".")) ?? [];
        if (owners.length <= 1) continue;
        const rest = segs.slice(i).join(".");
        // Only owners that actually CARRY the suffix count — the graph
        // registers each spaced interface path under its `Space::Path` alias.
        const spellings = owners
          .map((o) => `${spacedSpelling(o)}.${rest}`)
          .filter((s) => idx().interfacesByPath.has(s));
        if (spellings.length > 1) return spellings;
      }
      return [];
    },

    processFor(id: string): ProcessLocation | undefined {
      const raw = id.startsWith("#") ? id.slice(1) : id;
      const decl = procIdx().get(raw) ?? procIdx().get(id);
      if (!decl) return undefined;
      return {
        kind: "process",
        decl,
        file: decl.source?.file,
        span: decl.source,
        extent: decl.extentSource ?? decl.source,
        nameSpan: decl.nameSource,
        materialization: isZeroSpan(decl.source) ? "stub" : "ready",
      };
    },
  };
}
